import { Bike, Map, MapPin, Menu, Users } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as routes from "@/constants/routes";
import { toV2Route } from "@/constants/routes";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/v2/hooks/useMediaQuery";
import { Button } from "@/v2/ui/primitives/button";

export interface BottomNavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  dataTour?: string;
}

export interface BottomNavigationProps {
  className?: string;
  items?: BottomNavigationItem[];
  onMenuClick?: () => void;
}

const defaultItems: BottomNavigationItem[] = [
  {
    id: "menu",
    label: "Menu",
    icon: Menu,
    route: "", // Special case for menu button
    dataTour: "bottom-nav-menu",
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: Map,
    route: routes.DASHBOARD_SCREEN,
    dataTour: "bottom-nav-dashboard",
  },
  {
    id: "bikes",
    label: "Bikes",
    icon: Bike,
    route: routes.DASHBOARD_BIKES,
    dataTour: "bottom-nav-bikes",
  },
  {
    id: "stations",
    label: "Stations",
    icon: MapPin,
    route: routes.DASHBOARD_STATIONS,
    dataTour: "bottom-nav-stations",
  },
  {
    id: "users",
    label: "Users",
    icon: Users,
    route: routes.DASHBOARD_USERS,
    dataTour: "bottom-nav-users",
  },
];

const BottomNavigation = React.forwardRef<
  HTMLDivElement,
  BottomNavigationProps
>(({ className, items = defaultItems, onMenuClick }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const normalizedPath = location.pathname.replace("/v2", "");

  if (!isMobile) {
    return null;
  }

  const handleItemClick = (item: BottomNavigationItem) => {
    if (item.id === "menu" && onMenuClick) {
      onMenuClick();
    } else if (item.route) {
      navigate(toV2Route(item.route));
    }
  };

  const isItemActive = (item: BottomNavigationItem) => {
    if (item.id === "menu") {
      return false; // Menu button is an action, not a route
    }
    if (item.route === routes.DASHBOARD_SCREEN) {
      return normalizedPath === routes.DASHBOARD_SCREEN;
    }
    return (
      normalizedPath === item.route ||
      normalizedPath.startsWith(item.route + "/")
    );
  };

  return (
    <div
      ref={ref}
      className={cn(
        "tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50",
        "tw-bg-white dark:tw-bg-slate-950 tw-border-t tw-border-border",
        "tw-shadow-[0_-4px_12px_rgba(0,0,0,0.08)]",
        "tw-safe-area-bottom",
        className
      )}
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0)",
      }}
    >
      <div className="tw-flex tw-items-center tw-justify-around tw-px-2 tw-py-3">
        {items.map((item) => {
          const active = isItemActive(item);
          const Icon = item.icon;

          return (
            <Button
              key={item.id}
              variant="ghost"
              size="sm"
              data-tour={item.dataTour}
              className={cn(
                "tw-flex tw-flex-col tw-items-center tw-justify-center",
                "tw-h-auto tw-px-2 tw-py-2.5",
                "tw-min-w-[64px] tw-max-w-[80px]",
                "tw-rounded-lg",
                "tw-transition-all tw-duration-200",
                active ? "tw-bg-primary/10" : "hover:tw-bg-muted"
              )}
              onClick={() => handleItemClick(item)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
            >
              <div className="tw-relative">
                <Icon
                  className={cn(
                    "tw-h-5 tw-w-5 tw-mb-1",
                    "tw-transition-colors tw-duration-200",
                    active ? "tw-text-primary" : "tw-text-muted-foreground"
                  )}
                />
              </div>
              <span
                className={cn(
                  "tw-text-[10px] tw-font-medium tw-tracking-tight",
                  "tw-truncate tw-max-w-full",
                  "tw-transition-colors tw-duration-200",
                  active
                    ? "tw-text-primary tw-font-semibold"
                    : "tw-text-muted-foreground"
                )}
              >
                {item.label}
              </span>

              {/* Active indicator dot */}
              {active && (
                <div className="tw-absolute tw-top-1 tw-h-1 tw-w-1 tw-rounded-full tw-bg-primary" />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
});

BottomNavigation.displayName = "BottomNavigation";

export { BottomNavigation };
